import { FunctionsHttpError } from '@supabase/supabase-js';
import { getActivePortalKind, getDbSupabase, type DbPortalKind } from './supabase';

export type SharePointPortalKind = Extract<DbPortalKind, 'crm' | 'it'>;

export function formatSharePointError(error: unknown): string {
  if (!error) return 'SharePoint sync failed.';
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (typeof error === 'object') {
    const row = error as Record<string, unknown>;
    if (typeof row.message === 'string' && row.message.trim()) return row.message;
    if (typeof row.error === 'string' && row.error.trim()) return row.error;
    try {
      return JSON.stringify(error);
    } catch {
      return 'SharePoint sync failed.';
    }
  }
  return String(error);
}

async function readFunctionsHttpError(error: FunctionsHttpError): Promise<string> {
  try {
    const body = await error.context.json();
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      const row = body as Record<string, unknown>;
      if (typeof row.error === 'string' && row.error.trim()) return row.error;
      if (typeof row.message === 'string' && row.message.trim()) return row.message;
      return JSON.stringify(body);
    }
  } catch {
    // fall through
  }
  return error.message || 'Edge Function returned a non-2xx status code';
}

export interface SharePointSettings {
  enabled: boolean;
  site_hostname: string;
  site_path: string;
  folder_path: string;
  sheet_name: string;
  current_workbook_month: string | null;
  current_workbook_item_id: string | null;
  current_workbook_name: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  updated_at?: string;
}

export async function fetchSharePointSettings(
  portalKind: SharePointPortalKind = 'crm',
): Promise<SharePointSettings | null> {
  const { data, error } = await getDbSupabase(portalKind)
    .from('crm_sharepoint_settings')
    .select(
      'enabled, site_hostname, site_path, folder_path, sheet_name, current_workbook_month, current_workbook_item_id, current_workbook_name, last_sync_at, last_error, updated_at',
    )
    .eq('id', 1)
    .maybeSingle();
  if (error) throw error;
  return (data as SharePointSettings | null) ?? null;
}

export async function saveSharePointSettings(
  input: {
    enabled: boolean;
    site_hostname: string;
    site_path: string;
    folder_path: string;
  },
  portalKind: SharePointPortalKind = 'crm',
): Promise<SharePointSettings> {
  const { data, error } = await getDbSupabase(portalKind).rpc('save_sharepoint_settings', {
    p_enabled: input.enabled,
    p_site_hostname: input.site_hostname,
    p_site_path: input.site_path,
    p_folder_path: input.folder_path,
  });
  if (error) throw error;
  return data as SharePointSettings;
}

export type SharePointSyncAction = 'sync' | 'test' | 'monthly_rollover' | 'diagnose';

export async function clearSharePointWorkbookPointers(
  portalKind: SharePointPortalKind = 'crm',
): Promise<void> {
  const { error } = await getDbSupabase(portalKind)
    .from('crm_sharepoint_settings')
    .update({
      current_workbook_month: null,
      current_workbook_item_id: null,
      current_workbook_name: null,
    })
    .eq('id', 1);
  if (error) throw error;
}

export async function invokeSharePointEnquirySync(
  action: SharePointSyncAction = 'sync',
  portalKind: SharePointPortalKind = 'crm',
) {
  const { data, error } = await getDbSupabase(portalKind).functions.invoke(
    `sharepoint-enquiry-sync?portal=${portalKind}`,
    {
      body: { action, portal: portalKind },
      headers: { 'x-bth-portal': portalKind },
    },
  );

  if (error) {
    if (error instanceof FunctionsHttpError) {
      throw new Error(await readFunctionsHttpError(error));
    }
    throw new Error(formatSharePointError(error));
  }

  if (data && typeof data === 'object' && 'error' in data) {
    const row = data as Record<string, unknown>;
    if (row.error) throw new Error(formatSharePointError(row.error));
  }

  return data;
}

function resolveSharePointPortalKind(portalKind?: SharePointPortalKind): SharePointPortalKind {
  if (portalKind) return portalKind;
  const active = getActivePortalKind();
  return active === 'it' ? 'it' : 'crm';
}

/** Fire-and-forget sync after project changes; never blocks UI on failure. */
export function queueSharePointEnquirySync(portalKind?: SharePointPortalKind) {
  const kind = resolveSharePointPortalKind(portalKind);
  void invokeSharePointEnquirySync('sync', kind).catch((err) => {
    console.warn('[SharePoint sync]', err);
  });
}

export function parseSharePointSiteInput(raw: string): {
  site_hostname: string;
  site_path: string;
} {
  const trimmed = raw.trim();
  if (!trimmed) return { site_hostname: '', site_path: '' };

  try {
    const url = new URL(trimmed);
    const hostname = url.hostname;
    const sitePath = url.pathname.replace(/\/$/, '');
    return { site_hostname: hostname, site_path: sitePath };
  } catch {
    if (trimmed.includes('/')) {
      const [host, ...rest] = trimmed.split('/');
      return {
        site_hostname: host,
        site_path: `/${rest.join('/')}`.replace(/\/$/, ''),
      };
    }
    return { site_hostname: trimmed, site_path: '' };
  }
}

export function formatSharePointSiteInput(settings: SharePointSettings): string {
  if (!settings.site_hostname) return '';
  if (settings.site_path) {
    return `https://${settings.site_hostname}${settings.site_path}`;
  }
  return settings.site_hostname;
}

export function sharePointPortalLabel(portalKind: SharePointPortalKind): string {
  return portalKind === 'it' ? 'IT Management' : 'Sales CRM';
}

const CRM_TIMEZONE = 'Asia/Singapore';

/** Expected monthly workbook name for the active portal (matches edge function). */
export function expectedMonthlyWorkbookFileName(
  portalKind: SharePointPortalKind,
  date = new Date(),
): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CRM_TIMEZONE,
    month: '2-digit',
    year: '2-digit',
  }).formatToParts(date);
  const mm = parts.find((p) => p.type === 'month')?.value ?? '01';
  const yy = parts.find((p) => p.type === 'year')?.value ?? '00';
  const mon = new Intl.DateTimeFormat('en-US', {
    timeZone: CRM_TIMEZONE,
    month: 'short',
  }).format(date);
  const label = portalKind === 'it' ? 'IT' : 'Sales';
  return `${mm}. project updates by ${label} - ${mon}'${yy}.xlsx`;
}

export function workbookNameMatchesPortal(
  name: string | null | undefined,
  portalKind: SharePointPortalKind,
): boolean {
  if (!name?.trim()) return false;
  const lower = name.toLowerCase();
  const label = portalKind === 'it' ? 'it' : 'sales';
  return lower.includes(`project updates by ${label}`);
}
